import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {RootState} from '../../redux/store';
import GenericButton from '../../components/button';
import {FiltersModalProps} from '../../interfaces/connected-components/filtersModal';
import {Theme} from '../../theme/lightTheme';
import TextGeneric from '../../components/textGeneric';
import {tr} from '../../translations';
import {closeFiltersModal} from './actions';
import Icon from '../../components/icon';
import {handleChange} from '../../utils/form';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  BackHandler,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  TouchableOpacity,
} from 'react-native';
import useKeyboardStatus from '../../utils/hooks/useKeyboardStatus';
import TextInput from '../../components/textInput';
import {getFontFamily} from '../../utils/helpers';
import FiltersModalSkeleton from '../../components/filtersModalSkeleton';
import {openDateRangePickerModal} from '../dateRangePickerModal/actions';
import dayjs, {Dayjs} from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import {FilterType} from '../../interfaces/reducers/homeState';
import {toggleSettingsModal} from '../settingsModal/actions';
import FiltersSheet from '../../components/filtersSheet';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
dayjs.extend(minMax);

// Mobile-specific styles
const ModalView = styled.View<{responsiveWidth: number}>`
  flex: 1;
  height: 100%;
  width: ${({responsiveWidth}) => responsiveWidth}px;
  position: absolute;
  top: 0;
`;
const Container = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const ContentContainer = styled.View<{responsiveWidth: number}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  align-self: center;
  border-radius: 8px;
  background-color: ${({theme}) => theme.homeBackground};
  padding: ${({theme}) => theme.text.s7}px ${({theme}) => theme.text.s6}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 4;
`;
const Row = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({theme}) => theme.text.s4}px;
`;
const IconsContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;
const FiltersContainer = styled.View`
  width: 100%;
  padding-bottom: ${({theme}) => theme.text.s55}px;
  z-index: 10004;
  overflow: visible;
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s6}px;
  text-align: center;
`;
const SubmitButton = styled(GenericButton)<{responsiveWidth: string}>`
  width: ${({responsiveWidth}) => responsiveWidth};
  align-self: center;
  z-index: 11;
`;
const AllFiltersButton = styled(GenericButton)`
  width: 80%;
  align-self: center;
  z-index: 11;
  margin-top: 4%;
`;
const Overlay = styled.Pressable<{
  responsiveWidth: number;
  responsiveHeight: number;
}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  height: ${({responsiveHeight}) => responsiveHeight}px;
  align-self: center;
  position: absolute;
  top: 0;
`;

const FiltersModal = ({filtersModal}: FiltersModalProps) => {
  const isKeyboardOpen = useKeyboardStatus();
  const theme = useTheme() as Theme;
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const screenHeight = hdp(100);

  const {isVisible, onSubmit, filters, filtersValues} = filtersModal || {};
  const [form, updateForm] = useState<any>({});
  const [modalVisible, setModalVisible] = useState(false);
  const overlayOpacity = useSharedValue(0);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  // Handle back button press
  const handleBackPress = useCallback(() => {
    if (isVisible) {
      closeFiltersModal();
      return true;
    }
    return false;
  }, [isVisible]);

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  const submitHandler = (isReset = false) => {
    if (onSubmit) {
      onSubmit(isReset ? {} : form);
    }
    closeFiltersModal();
    updateForm({});
  };
  useEffect(() => {
    overlayOpacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    opacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    translateY.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    if (isVisible) {
      setModalVisible(true);
      updateForm(filtersValues);
    } else {
      setTimeout(() => setModalVisible(false), 300);
      updateForm({});
    }
  }, [isVisible]);

  const overlayStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: `rgba(0, 0, 0, ${overlayOpacity.value * 0.2})`,
    };
  });
  const translateStyle = useAnimatedStyle(() => {
    const translate = interpolate(translateY.value, [0, 1], [screenHeight, 0]);
    return {
      transform: [{translateY: translate}],
    };
  });

  if (!modalVisible) {
    return null;
  }

  const getValue = (filterItem: FilterType) => {
    const id = filterItem?.key ? form[filterItem?.key] : -1;
    const idx = (filterItem?.items || []).findIndex(i => i?.id === id);
    if (idx > -1) {
      return filterItem?.items?.[idx]?.value;
    }
    return '';
  };

  return (
    <>
      <ModalView responsiveWidth={wdp(100)}>
        <KeyboardAvoidingView
          enabled={Platform.OS === 'ios'}
          behavior={'padding'}
          style={{
            flex: 1,
          }}>
          <Pressable
            style={{
              flex: 1,
            }}
            onPress={() => {
              isKeyboardOpen && Keyboard.dismiss();
            }}>
            <Animated.View
              style={[
                {
                  flex: 1,
                },
                overlayStyle,
              ]}>
              <Animated.View
                style={[
                  translateStyle,
                  {
                    height: '100%',
                    width: '100%',
                    alignSelf: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                ]}>
                <Container>
                  <Overlay
                    responsiveWidth={wdp(100)}
                    responsiveHeight={hdp(100)}
                    onPress={() => {
                      closeFiltersModal();
                    }}
                  />
                  <ContentContainer
                    responsiveWidth={wdp(getByScreenSize(90, 50))}>
                    <Row>
                      <Title bold withGradient>
                        {tr('filtersModal.title')}
                      </Title>
                      <IconsContainer>
                        <Icon
                          onPress={() => submitHandler(true)}
                          name={'filterRemove'}
                          size={theme.text.s4}
                          color={theme.text.error + '99'}
                          role={'button'}
                        />
                        <Icon
                          onPress={closeFiltersModal}
                          name={'x'}
                          size={theme.text.s6}
                          color={theme.text.dark + '88'}
                          role={'button'}
                        />
                      </IconsContainer>
                    </Row>
                    <FiltersContainer>
                      {(filters || []).length === 0 && <FiltersModalSkeleton />}
                      {(filters || []).map(filterItem => {
                        let dates: Array<Dayjs> = [];
                        if (filterItem.key === 'date') {
                          dates = (filterItem?.items || [])
                            .map(date =>
                              dayjs(date.value).isValid()
                                ? dayjs(date.value)
                                : null,
                            )
                            .filter((d): d is Dayjs => d !== null);
                        }
                        if (
                          (filterItem?.items || []).length <= 1 &&
                          filterItem.key !== 'date'
                        ) {
                          return null;
                        }

                        return (
                          <TouchableOpacity
                            onPress={() => {
                              if (filterItem.key === 'date') {
                                openDateRangePickerModal({
                                  onConfirm: val => {
                                    if (val?.date) {
                                      const date = dayjs(val?.date);
                                      handleChange(
                                        filterItem?.key || '',
                                        date,
                                        updateForm,
                                      );
                                    }
                                  },
                                  datePickerProps: {
                                    mode: 'single',
                                    disabledDates: (date: any) =>
                                      !(dates || []).some(enabledDate =>
                                        enabledDate.isSame(date, 'day'),
                                      ),
                                    minDate: dayjs.min(dates),
                                    maxDate: dayjs.max(dates),
                                  },
                                  currentRange: {
                                    //@ts-ignore
                                    date: getValue(filterItem)
                                      ? dayjs(getValue(filterItem))
                                      : undefined,
                                  },
                                });
                                return;
                              }
                              toggleSettingsModal({
                                title:
                                  tr('tabNews.select') +
                                  ' ' +
                                  (filterItem?.label || ''),
                                component: () => (
                                  <FiltersSheet
                                    filters={filterItem?.items || []}
                                    selectedFilterId={
                                      form?.[filterItem?.key || '']
                                    }
                                    onItemPress={item =>
                                      handleChange(
                                        filterItem?.key || '',
                                        item?.id,
                                        updateForm,
                                      )
                                    }
                                  />
                                ),
                              });
                            }}
                            key={filterItem?.key}>
                            <TextInput
                              pointerEvents={'none'}
                              editable={false}
                              value={
                                filterItem.key === 'date' && form.date
                                  ? dayjs(form.date).format('YYYY-MM-DD')
                                  : getValue(filterItem) || filterItem?.label
                              }
                              containerStyle={{
                                marginBottom: theme.text.s55,
                              }}
                              inputContainerStyle={{
                                borderColor: theme.primary,
                              }}
                              style={{
                                fontFamily: getFontFamily().semi_bold,
                                width: '90%',
                              }}
                              showRightIcon
                              rightIconName={'arrowDown'}
                              rightIconSize={theme.text.s3}
                              rightIconColor={theme.primary}
                              numberOfLines={1}
                            />
                          </TouchableOpacity>
                        );
                      })}
                    </FiltersContainer>
                    <SubmitButton
                      responsiveWidth={getByScreenSize('50%', '30%')}
                      isSmall
                      label={tr('app.ok')}
                      onPress={() => submitHandler()}
                    />
                    <AllFiltersButton
                      outline
                      isSmall
                      label={tr('filtersModal.allFilters')}
                      labelStyle={{
                        color: theme.text.dark,
                      }}
                      onPress={() => submitHandler(true)}
                    />
                  </ContentContainer>
                </Container>
              </Animated.View>
            </Animated.View>
          </Pressable>
        </KeyboardAvoidingView>
      </ModalView>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  filtersModal: state.filtersModal,
});

export default connect(mapStateToProps, null)(FiltersModal);
